import { cloneElement, type MouseEvent, type MouseEventHandler, type ReactElement, useState } from 'react';

export type HoverElement = ((state: boolean) => ReactElement<HoverElementProps>) | ReactElement<HoverElementProps>;

export interface HoverElementProps {
  onMouseEnter?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
}

/**
 * This is basically a fork of `useHover` from `react-use` which is no longer actively maintained and unsafe to import
 * in a Node environment. For docs, see:
 *
 * https://github.com/streamich/react-use/blob/master/docs/useHover.md
 */
export const useHover = (element: HoverElement): [ReactElement<unknown>, boolean] => {
  const [state, setState] = useState(false);

  const onMouseEnter = (originalOnMouseEnter?: MouseEventHandler) => (event: MouseEvent) => {
    originalOnMouseEnter?.(event);
    setState(true);
  };

  const onMouseLeave = (originalOnMouseLeave?: MouseEventHandler) => (event: MouseEvent) => {
    originalOnMouseLeave?.(event);
    setState(false);
  };

  if (typeof element === 'function') {
    element = element(state);
  }

  const el = cloneElement(element, {
    onMouseEnter: onMouseEnter(element.props.onMouseEnter),
    onMouseLeave: onMouseLeave(element.props.onMouseLeave),
  });

  return [el, state];
};
